import React, { useEffect, useState } from 'react';

// ──────────────────────────────────────────────────────────────────────
// Writing report — mobile-first, dark theme.
// Renders the v3 backend payload (feedback_json on each writing_response).
// Tolerant fallbacks for older v2 data so historical reports still display.
// Ported from the FET app's FETWritingReportView for visual parity.
// ──────────────────────────────────────────────────────────────────────

const CRIT_DEFAULTS = {
  'Content & Communicative Achievement': { short: 'Content', color: '#3b82f6', icon: '💬' },
  'Language & Organisation':              { short: 'Language', color: '#22c55e', icon: '🔗' },
  Organisation:                           { short: 'Organisation', color: '#22c55e', icon: '🔗' },
  Vocabulary:                             { short: 'Vocabulary', color: '#f59e0b', icon: '📖' },
  Grammar:                                { short: 'Grammar', color: '#ec4899', icon: '📝' },
};

const colorForBand = (score, max) => {
  const ratio = max ? score / max : 0;
  if (ratio >= 0.8) return '#22c55e';
  if (ratio >= 0.7) return '#3b82f6';
  return '#f59e0b';
};

const countWords = (text = '') => {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
};

const formatAttemptDate = (value) => {
  if (!value) return 'Recently';
  try {
    return new Date(value).toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  } catch {
    return 'Recently';
  }
};

// ── Animated score ring ──
function Ring({ score, max, color, size = 72, stroke = 5, delay = 0 }) {
  const [progress, setProgress] = useState(0);
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  useEffect(() => {
    const timer = setTimeout(() => {
      let start = null;
      const target = max ? (score / max) * 100 : 0;
      const tick = (ts) => {
        if (start === null) start = ts;
        const t = Math.min((ts - start) / 1200, 1);
        setProgress((1 - Math.pow(1 - t, 3)) * target);
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, delay);
    return () => clearTimeout(timer);
  }, [score, max, delay]);
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - (progress / 100) * circumference}
          strokeLinecap="round"
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: size * 0.32, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: size * 0.15, color: 'rgba(255,255,255,0.3)' }}>/{max}</span>
      </div>
    </div>
  );
}

// ── Band-comparison row (collapsible) ──
function BandCompare({ criterion }) {
  const [open, setOpen] = useState(false);
  const c = criterion;
  return (
    <div style={{ marginBottom: 12 }}>
      <div onClick={() => setOpen(!open)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, cursor: 'pointer' }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: '#d1d5db' }}>{c.short || c.name}</span>
        <span style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>
          {c.score}<span style={{ fontSize: 12, color: '#4b5563' }}>/{c.maxScore}</span>
        </span>
        <span style={{ color: '#4b5563', fontSize: 10, transform: open ? 'rotate(180deg)' : '', transition: 'transform 0.3s' }}>▼</span>
      </div>
      {open && (
        <div style={{ margin: '8px 0 0 0', borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.03)' }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
              Your level now — Band {c.score}
            </div>
            <div style={{ fontSize: 13, color: '#9ca3af', lineHeight: 1.5 }}>{c.bandNow || 'Rubric description not available.'}</div>
            {c.bandNowAr && (
              <div style={{ fontSize: 12, color: '#60a5fa', direction: 'rtl', textAlign: 'right', marginTop: 6, lineHeight: 1.5 }}>{c.bandNowAr}</div>
            )}
          </div>
          <div style={{ padding: '12px 14px', background: 'rgba(34,197,94,0.05)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: '#22c55e', textTransform: 'uppercase', marginBottom: 6 }}>
              Target — Band {Math.min(5, (c.score || 0) + 1)}
            </div>
            <div style={{ fontSize: 13, color: '#86efac', lineHeight: 1.5 }}>{c.bandNext || 'Aim for the next band up.'}</div>
            {c.bandNextAr && (
              <div style={{ fontSize: 12, color: '#60a5fa', direction: 'rtl', textAlign: 'right', marginTop: 6, lineHeight: 1.5 }}>{c.bandNextAr}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Single improvement card with before/after toggle + swaps + practice task ──
function Fix({ fix }) {
  const [view, setView] = useState('before');
  const [open, setOpen] = useState(true);

  const fromBand = Number.isFinite(Number(fix.fromBand)) ? Number(fix.fromBand) : null;
  const toBand = Number.isFinite(Number(fix.toBand))
    ? Number(fix.toBand)
    : (fromBand !== null ? Math.min(5, fromBand + (Number(fix.marksGained) || 1)) : null);
  const gain = Number(fix.marksGained) || (toBand !== null && fromBand !== null ? toBand - fromBand : 0);

  return (
    <div style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, overflow: 'hidden', marginBottom: 14 }}>
      <div onClick={() => setOpen(!open)} style={{ padding: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: `${fix.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
          {fix.icon || '✦'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, color: fix.color }}>{fix.criterionName}</span>
            {gain > 0 && (
              <span style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80', padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 800 }}>
                +{gain}
              </span>
            )}
            {fromBand !== null && toBand !== null && (
              <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                Band {fromBand} → {toBand}
              </span>
            )}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{fix.action || 'Improvement'}</div>
        </div>
        <div style={{ color: '#4b5563', fontSize: 12, transform: open ? 'rotate(180deg)' : '', transition: 'transform 0.3s', flexShrink: 0 }}>▼</div>
      </div>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {fix.rule && (
            <div style={{ background: `${fix.color}10`, borderLeft: `3px solid ${fix.color}`, borderRadius: '0 10px 10px 0', padding: '10px 14px', marginBottom: 14, fontSize: 14, color: '#e0e6ed', fontWeight: 600 }}>
              {fix.rule}
            </div>
          )}

          {(fix.before || fix.after) && (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {['before', 'after'].map((v) => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    style={{
                      flex: 1,
                      padding: '10px 0',
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      transition: 'all 0.2s',
                      border: view === v ? `2px solid ${v === 'before' ? '#ef4444' : '#22c55e'}` : '1px solid rgba(255,255,255,0.1)',
                      background: view === v ? (v === 'before' ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)') : 'transparent',
                      color: view === v ? (v === 'before' ? '#fca5a5' : '#86efac') : '#6b7280',
                      fontFamily: 'inherit',
                    }}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <div style={{ background: 'rgba(0,0,0,0.45)', borderRadius: 12, padding: '16px', fontSize: 15, lineHeight: 1.8, borderLeft: `4px solid ${view === 'before' ? '#ef4444' : '#22c55e'}`, transition: 'border-color 0.3s' }}>
                {view === 'before'
                  ? <span style={{ color: '#fca5a5', textDecoration: 'line-through', textDecorationColor: 'rgba(252,165,165,0.4)' }}>{fix.before || '—'}</span>
                  : <span style={{ color: '#86efac' }}>{fix.after || '—'}</span>
                }
              </div>
            </>
          )}

          {Array.isArray(fix.swaps) && fix.swaps.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
              {fix.swaps.map((s, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '8px 12px' }}>
                  <span style={{ fontSize: 13, color: '#fca5a5', textDecoration: 'line-through' }}>{s.old}</span>
                  <span style={{ color: '#4ade80', fontSize: 14 }}>→</span>
                  <span style={{ fontSize: 13, color: '#86efac', fontWeight: 600 }}>{s.new}</span>
                </div>
              ))}
            </div>
          )}

          {fix.arabic && (
            <div style={{ background: 'rgba(59,130,246,0.08)', borderRight: '3px solid #3b82f6', borderRadius: 8, padding: '10px 14px', marginTop: 12, direction: 'rtl', textAlign: 'right' }}>
              <span style={{ fontSize: 13, color: '#93c5fd', lineHeight: 1.6 }}>{fix.arabic}</span>
            </div>
          )}

          {fix.practiceTask && (
            <div style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.1), rgba(251,191,36,0.03))', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 12, padding: '14px 16px', marginTop: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true">✏️</span>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                  Try now — {fix.practiceTime || 5} min
                </div>
                <div style={{ fontSize: 13, color: '#fde68a', lineHeight: 1.5 }}>{fix.practiceTask}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── One task block (Part 1 or Part 2) ──
function TaskBlock({ task, delayIndex, attemptResponse, forceShowAnswer }) {
  const [showBands, setShowBands] = useState(false);
  const [showAns, setShowAns] = useState(false);
  const answerVisible = forceShowAnswer || showAns;

  const score = Number(task.total || 0);
  const max = Number(task.max_total || 0);
  const ringColor = colorForBand(score, max);
  const potential = Number.isFinite(Number(task.potential_score)) ? Number(task.potential_score) : null;
  const fb = task.feedback_json && typeof task.feedback_json === 'object' ? task.feedback_json : {};

  // criteria — prefer v3 feedback_json.criteria; fall back to building from task.scores
  const criteria = (Array.isArray(fb.criteria) && fb.criteria.length)
    ? fb.criteria
    : (() => {
        const rows = task.part === 1
          ? [['Content & Communicative Achievement', task.scores?.content], ['Language & Organisation', task.scores?.language]]
          : [
              ['Content & Communicative Achievement', task.scores?.content],
              ['Organisation', task.scores?.communicative],
              ['Vocabulary', task.scores?.organisation],
              ['Grammar', task.scores?.language],
            ];
        return rows.map(([name, val]) => {
          const d = CRIT_DEFAULTS[name] || {};
          return { name, short: d.short || name, score: Number(val || 0), maxScore: 5, color: d.color || '#6b7280', icon: d.icon || '' };
        });
      })();

  // improvements — prefer v3 sanitised improvements (with swaps/icon/color/etc.); fall back to legacy detail array
  const improvements = (Array.isArray(fb.improvements) && fb.improvements.length)
    ? fb.improvements
    : (Array.isArray(task.improvements_detail) ? task.improvements_detail : []);

  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ padding: '20px 16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.5, color: '#4b5563' }}>
              Task {task.part || delayIndex + 1}
            </div>
            <h2 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {task.label || 'Writing Task'}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <Ring score={score} max={max} color={ringColor} size={58} stroke={4} delay={delayIndex * 200} />
            {potential !== null && potential > score && (
              <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 10, padding: '6px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#4ade80', lineHeight: 1 }}>↑{potential}</div>
                <div style={{ fontSize: 8, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>possible</div>
              </div>
            )}
          </div>
        </div>

        {(task.well_done || fb.wellDone) && (
          <div style={{ display: 'flex', gap: 10, background: 'linear-gradient(135deg, rgba(34,197,94,0.1), rgba(34,197,94,0.03))', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 14, padding: '14px', marginBottom: 18 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }} aria-hidden="true">✅</span>
            <p style={{ margin: 0, fontSize: 14, color: '#bbf7d0', lineHeight: 1.5, fontWeight: 500 }}>
              {task.well_done || fb.wellDone}
            </p>
          </div>
        )}

        {improvements.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
              <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.2, color: '#6b7280' }}>How to get more marks</span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
            </div>
            {improvements.map((imp, idx) => (
              <Fix key={`${task.id}-fix-${idx}`} fix={imp} />
            ))}
          </>
        )}

        {/* Legacy fallback: pre-v3 attempts only had a global practice task */}
        {improvements.length === 0 && task.practice_task && (
          <div style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.1), rgba(251,191,36,0.03))', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 12, padding: '14px 16px', marginTop: 12, display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 18 }}>
            <span style={{ fontSize: 16, flexShrink: 0 }} aria-hidden="true">✏️</span>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Try now — 5 min</div>
              <div style={{ fontSize: 13, color: '#fde68a', lineHeight: 1.5 }}>{task.practice_task}</div>
            </div>
          </div>
        )}

        {task.zero_reason && (
          <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, padding: '12px 14px', marginBottom: 18, color: '#fca5a5', fontSize: 13, lineHeight: 1.6 }}>
            <strong style={{ color: '#fecaca' }}>Note: </strong>{task.zero_reason}
          </div>
        )}
      </div>

      {/* Rubric breakdown */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div onClick={() => setShowBands(!showBands)} style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#4b5563' }}>{showBands ? 'Hide' : 'Show'} rubric breakdown</span>
          <span style={{ color: '#4b5563', fontSize: 10, transform: showBands ? 'rotate(180deg)' : '', transition: 'transform 0.3s' }}>▼</span>
        </div>
        {showBands && (
          <div style={{ padding: '0 16px 16px' }}>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10, textAlign: 'center' }}>Tap each to see your band vs next band</div>
            {criteria.map((c, idx) => (
              <BandCompare key={`${task.id}-band-${idx}`} criterion={c} />
            ))}
          </div>
        )}
      </div>

      {/* Submitted answer */}
      {attemptResponse?.text && (
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {!forceShowAnswer && (
            <div onClick={() => setShowAns(!showAns)} style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#4b5563' }}>{showAns ? 'Hide' : 'Show'} your answer</span>
              <span style={{ color: '#4b5563', fontSize: 10, transform: showAns ? 'rotate(180deg)' : '', transition: 'transform 0.3s' }}>▼</span>
            </div>
          )}
          {answerVisible && (
            <div style={{ padding: '0 16px 16px' }}>
              <div style={{ fontSize: 11, color: '#4b5563', marginBottom: 8 }}>{countWords(attemptResponse.text)} words · {formatAttemptDate(attemptResponse.submitted_at)}</div>
              <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 12, padding: '16px', fontSize: 14, color: '#d1d5db', lineHeight: 1.8, whiteSpace: 'pre-line' }}>{attemptResponse.text}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Collapsible reference guide section ──
function RefSection({ icon, title, tag, tagColor, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, overflow: 'hidden', marginBottom: 10 }}>
      <div onClick={() => setOpen(!open)} style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16 }} aria-hidden="true">{icon}</span>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#fff' }}>{title}</span>
        <span style={{ fontSize: 10, color: tagColor, fontWeight: 800, background: `${tagColor}18`, padding: '3px 8px', borderRadius: 6 }}>{tag}</span>
        <span style={{ color: '#4b5563', fontSize: 10, transform: open ? 'rotate(180deg)' : '', transition: 'transform 0.3s' }}>▼</span>
      </div>
      {open && <div style={{ padding: '0 16px 16px' }}>{children}</div>}
    </div>
  );
}

// Static reference content (same for every report — band 4→5 quick wins)
const QUICK_LINKS = [
  { w: 'because', eg: "I like it because it's fun.", c: '#3b82f6' },
  { w: 'and / but', eg: 'I tried but it was hard.', c: '#6b7280' },
  { w: 'so', eg: 'It rained, so I stayed.', c: '#ec4899' },
  { w: 'when / while', eg: 'When I saw it, I ran.', c: '#8b5cf6' },
  { w: 'during', eg: 'During the trip, I slept.', c: '#8b5cf6' },
  { w: 'after / before', eg: 'After school, I played.', c: '#06b6d4' },
  { w: 'although', eg: 'Although tired, I finished.', c: '#f59e0b' },
  { w: 'despite', eg: 'Despite rain, we went.', c: '#f59e0b' },
  { w: 'moreover', eg: 'Moreover, it was cheap.', c: '#f59e0b' },
  { w: 'as a result', eg: 'As a result, I passed.', c: '#f59e0b' },
];
const QUICK_VOCAB = [
  ['happy', 'delighted', 'thrilled'],
  ['sad', 'upset', 'devastated'],
  ['good', 'excellent', 'outstanding'],
  ['big', 'enormous', 'massive'],
  ['said', 'exclaimed', 'declared'],
  ['scared', 'terrified', 'petrified'],
  ['nice', 'pleasant', 'delightful'],
  ['went', 'rushed', 'ventured'],
];
const QUICK_GRAMMAR = [
  { p: 'Despite + noun, ...', eg: 'Despite the rain, we went.' },
  { p: '..., who + verb, ...', eg: 'The man, who was brave, helped.' },
  { p: 'Having + past participle', eg: 'Having finished, I left.' },
  { p: 'Although + clause', eg: 'Although tired, she ran.' },
];

export default function WritingReportView({
  report,
  responses = [],
  showSubmittedAnswers = false,
  onToggleSubmittedAnswers,
  footer,
  // legacy props (theme, isMobile, isTablet) are accepted but not used —
  // the new dark-gradient layout is self-styled and uses CSS media queries.
  ...rest
}) {
  void rest;
  if (!report) return null;

  if (report.mark_status !== 'done') {
    return (
      <div style={{ background: '#0c1232', borderRadius: 16, padding: 24, color: '#9ca3af', fontSize: 14, lineHeight: 1.7, textAlign: 'center' }}>
        {report.mark_status === 'marking'
          ? 'Marking in progress — your detailed report will appear here in a few seconds.'
          : 'This test did not finish marking successfully. Please try a new attempt.'}
      </div>
    );
  }

  const taskDetails = Array.isArray(report.task_details) ? report.task_details : [];
  const totalScore = Number(report.total || 0);
  const totalMax = Number(report.max_total || 30);

  // Pull aggregate display values from per-task data
  const studentLevel = taskDetails.find((t) => t.student_level)?.student_level || '';
  const totalPotential = taskDetails.reduce((acc, t) => {
    const p = Number(t.potential_score);
    return acc + (Number.isFinite(p) ? p : Number(t.total || 0));
  }, 0);
  const totalImprovements = taskDetails.reduce(
    (acc, t) => {
      const fb = (t.feedback_json && typeof t.feedback_json === 'object') ? t.feedback_json : {};
      const list = Array.isArray(fb.improvements) ? fb.improvements : (Array.isArray(t.improvements_detail) ? t.improvements_detail : []);
      return acc + list.length;
    },
    0,
  );

  // Map writing_responses to a lookup so each task block can show the submitted text
  const responseByPart = {};
  responses.forEach((r) => {
    if (r?.question_part != null) {
      responseByPart[r.question_part] = responseByPart[r.question_part] || r;
    }
  });

  return (
    <div style={{
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: 'linear-gradient(170deg, #060a1a 0%, #0c1232 50%, #101838 100%)',
      color: '#e0e6ed',
      minHeight: '100vh',
      margin: '-24px -16px',
      padding: '24px 12px',
      borderRadius: 12,
    }}>
      <style>{`
        @keyframes wrUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .wr-mobile-report * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        .wr-mobile-report { max-width: 480px; margin: 0 auto; }

        .wr-mr-hero { text-align: center; margin-bottom: 28px; animation: wrUp 0.6s ease; }
        .wr-mr-hero-info { display: flex; flex-direction: column; align-items: center; gap: 6px; }
        .wr-mr-tasks-row { display: flex; gap: 8px; margin-bottom: 22px; animation: wrUp 0.6s ease 0.15s both; }
        .wr-mr-task-grid > * { margin-bottom: 0; }
        .wr-mr-fixes { display: flex; flex-direction: column; gap: 0; }
        .wr-mr-refs-grid { display: flex; flex-direction: column; gap: 0; }
        .wr-mr-refs-grid > * { margin-bottom: 10px; }

        @media (min-width: 768px) {
          .wr-mobile-report { max-width: 760px; padding: 0 8px; }
          .wr-mr-hero {
            display: flex;
            align-items: center;
            gap: 36px;
            justify-content: center;
            text-align: left;
            padding: 12px 0 8px;
          }
          .wr-mr-hero-info { align-items: flex-start; gap: 10px; }
          .wr-mr-tasks-row { gap: 12px; }
        }

        @media (min-width: 1080px) {
          .wr-mobile-report { max-width: 1080px; }
          .wr-mr-task-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(460px, 1fr));
            gap: 18px;
            align-items: start;
          }
          .wr-mr-task-grid > * { margin-bottom: 0; }
          .wr-mr-fixes {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
          }
          .wr-mr-fixes > * { margin-bottom: 0; }
          .wr-mr-refs-grid {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 12px;
            align-items: start;
          }
          .wr-mr-refs-grid > * { margin-bottom: 0; }
        }
      `}</style>

      <div className="wr-mobile-report">
        {/* Hero score */}
        <div className="wr-mr-hero">
          <div>
            <div style={{ display: 'inline-block', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 20, padding: '4px 14px', fontSize: 10, fontWeight: 800, color: '#60a5fa', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 14 }}>
              Writing Result
            </div>
            <div>
              <Ring score={totalScore} max={totalMax} color={colorForBand(totalScore, totalMax)} size={130} stroke={8} delay={200} />
            </div>
          </div>

          <div className="wr-mr-hero-info">
            {studentLevel && (
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Level <span style={{ fontSize: 18, fontWeight: 800, color: '#60a5fa' }}>{studentLevel}</span>
              </div>
            )}

            <div style={{ fontSize: 28, fontWeight: 900, color: '#fff', letterSpacing: -0.5, lineHeight: 1 }}>
              {totalScore} <span style={{ fontSize: 16, color: '#4b5563', fontWeight: 700 }}>/ {totalMax}</span>
            </div>

            {totalPotential > totalScore && totalImprovements > 0 && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.15)', borderRadius: 12, padding: '10px 16px', marginTop: 4 }}>
                <span style={{ fontSize: 12, color: '#86efac' }}>You could get</span>
                <span style={{ fontSize: 22, fontWeight: 900, color: '#4ade80' }}>{totalPotential}/{totalMax}</span>
                <span style={{ fontSize: 12, color: '#86efac' }}>with {totalImprovements} change{totalImprovements === 1 ? '' : 's'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Per-task quick scores strip */}
        {taskDetails.length > 1 && (
          <div className="wr-mr-tasks-row">
            {taskDetails.map((t, i) => (
              <div key={`mini-${t.id || i}`} style={{ flex: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <Ring score={Number(t.total || 0)} max={Number(t.max_total || 0)} color={colorForBand(t.total || 0, t.max_total || 0)} size={42} stroke={3} delay={400 + i * 150} />
                <div>
                  <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1, color: '#4b5563' }}>Task {t.part || i + 1}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>
                    {Number(t.total || 0)}<span style={{ fontSize: 11, color: '#4b5563' }}>/{Number(t.max_total || 0)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Per-task detailed report — 2-up grid on wide desktop */}
        <div className="wr-mr-task-grid">
          {taskDetails.map((task, idx) => {
            const matched = responseByPart[task.part] || null;
            return (
              <div key={task.id || `task-${idx}`} style={{ animation: `wrUp 0.6s ease ${0.3 + idx * 0.2}s both` }}>
                <TaskBlock
                  task={task}
                  delayIndex={idx}
                  attemptResponse={matched}
                  forceShowAnswer={!!showSubmittedAnswers}
                />
              </div>
            );
          })}
        </div>

        {/* Optional global "Submitted Answers" toggle — kept for back-compat with the old Results.jsx UI. */}
        {responses.length > 0 && typeof onToggleSubmittedAnswers === 'function' && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <button
              onClick={onToggleSubmittedAnswers}
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#9ca3af',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.6,
                padding: '10px 16px',
                borderRadius: 999,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {showSubmittedAnswers ? 'Hide submitted answers ▲' : 'Show all submitted answers ▼'}
            </button>
          </div>
        )}

        {/* Reference guides — 3-col grid on wide desktop */}
        <div style={{ animation: 'wrUp 0.6s ease 0.8s both' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={{ width: 3, height: 16, borderRadius: 2, background: 'linear-gradient(to bottom, #8b5cf6, #3b82f6)' }} />
            <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.5, color: '#4b5563' }}>Quick Guides</span>
          </div>

          <div className="wr-mr-refs-grid">
            <RefSection icon="🔗" title="Linking Words" tag="Band 4→5" tagColor="#22c55e">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {QUICK_LINKS.map((l, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(0,0,0,0.25)', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ minWidth: 80 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: l.c }}>{l.w}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', flex: 1 }}>{l.eg}</div>
                  </div>
                ))}
              </div>
            </RefSection>

            <RefSection icon="📖" title="Word Upgrades" tag="Band 4→5" tagColor="#f59e0b">
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                  {['Basic ❌', 'Better ✓', 'Best ⭐'].map((h, i) => (
                    <div key={i} style={{ padding: 8, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', textAlign: 'center', background: i === 0 ? 'rgba(239,68,68,0.1)' : i === 1 ? 'rgba(59,130,246,0.1)' : 'rgba(34,197,94,0.1)', color: i === 0 ? '#fca5a5' : i === 1 ? '#93c5fd' : '#86efac' }}>{h}</div>
                  ))}
                </div>
                {QUICK_VOCAB.map((row, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                    {row.map((w, j) => (
                      <div key={j} style={{ padding: '9px 8px', fontSize: 13, textAlign: 'center', background: i % 2 === 0 ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.3)', color: j === 0 ? '#fca5a5' : j === 1 ? '#93c5fd' : '#86efac', fontWeight: j === 2 ? 700 : 400 }}>{w}</div>
                    ))}
                  </div>
                ))}
              </div>
            </RefSection>

            <RefSection icon="📝" title="Complex Sentences" tag="Band 4→5" tagColor="#ec4899">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {QUICK_GRAMMAR.map((g, i) => (
                  <div key={i} style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 10, padding: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#f472b6', marginBottom: 4 }}>{g.p}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>{g.eg}</div>
                  </div>
                ))}
              </div>
            </RefSection>
          </div>
        </div>

        {footer && (
          <div style={{ marginTop: 24 }}>
            {footer}
          </div>
        )}

        <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
          <p style={{ fontSize: 11, color: '#1f2937', margin: 0 }}>AI-powered feedback</p>
        </div>
      </div>
    </div>
  );
}
